import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ShipmentLocationViewModel } from '@components/shipment-route-list/shipment-route-map/shipment-route-map.component';
import {
  ShipmentStatusViewModel,
  ShipmentViewModel,
} from '../shipment-info-list-table/shipment-info-list-table.component';
import { ShipmentStatus } from '@components/shipment-list/shipment-status';
import { isBefore } from 'date-fns';
import { formatShipmentStayDuration } from '@utils/shipment.util';

/** ポイントタイプ */
type RoutePointType = 'startPoint' | 'arrivalPoint' | 'viaPoint' | 'inProgress';

/** ルート上のポイント */
type RoutePoint = {
  type: RoutePointType;
  pointId?: string;
  pointName?: string;
  completed?: boolean;
  // 時刻列の表示用。予定時刻(初期値)または実績時刻(eventAt)を保持する。
  timestamp?: string;
  // 滞在時間計算用の到着実績時刻(eventAt)。
  arrivedAt?: string;
  // 滞在時間計算用の出発実績時刻(eventAt)。
  departedAt?: string;
  stayDurationText?: string;
  displayButton?: boolean;
  currentLocation?: boolean;
};

@Component({
  selector: 'app-shipment-route-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    DatePipe,
  ],
  templateUrl: './shipment-route-table.component.html',
  styleUrl: './shipment-route-table.component.scss',
})
export class ShipmentRouteTableComponent implements OnInit, OnChanges {
  /** 配送 */
  @Input({ required: true }) shipment: ShipmentViewModel | undefined;

  /** 配送ステータス履歴 */
  @Input() shipmentStatusHistory: ShipmentStatusViewModel[] | null | undefined =
    [];

  /** 表示ソース */
  public dataSource = new MatTableDataSource<RoutePoint>([]);

  /** 詳細を開いているか */
  public expandedDetails: boolean = false;

  /** 表示カラム */
  public displayedTableColumns: string[] = [
    'displayButton',
    'timestamp',
    'routeLine',
    'status',
    'pointName',
    'currentLocation',
  ];

  ngOnInit(): void {
    this.initRoute();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['shipment'] !== undefined ||
      changes['shipmentStatusHistory'] !== undefined
    ) {
      this.initRoute();
    }
  }

  /**
   * 表示を切り替える
   *
   * @param flag 表示状態
   */
  public setDisplayViaLocation(flag: boolean): void {
    this.expandedDetails = flag;
  }

  /**
   * 行のスタイルクラスを作成する
   *
   * @param element 行要素
   * @returns スタイルクラス
   */
  public createRowClass(element: RoutePoint): string[] {
    const classList = [];

    if (element.displayButton === undefined) {
      classList.push('button-hidden');
    } else {
      classList.push('button-visible');
    }

    // 以下の場合、その経路行は表示するため、その逆の際にCSSクラスを付与する
    // ・出発地 or 到着地である
    // ・開閉ボタンである
    // ・経路が開いている
    // ・経由地が0件で、現在地である
    if (
      element.type !== 'startPoint' &&
      element.type !== 'arrivalPoint' &&
      !element.displayButton &&
      !this.expandedDetails &&
      !(this.viaPointLength === 0 && element.currentLocation)
    ) {
      classList.push('row-hidden');
    }

    return classList;
  }

  /**
   * 表示状態を加味した現在位置であるか
   *
   * @param element 行要素
   * @returns trueなら現在位置
   */
  public isCurrentLocation(element: RoutePoint): boolean {
    // ボタン表示箇所か、あるいは、展開状態であれば、その行の値をそのまま返す
    // それ以外は、畳んだ経由地や途中にいれば、Trueとする
    if (!element.displayButton || this.expandedDetails) {
      return !!element.currentLocation;
    }

    const currentRoute = this.dataSource.data.find(
      (route) => route.currentLocation === true,
    );
    return (
      currentRoute?.type === 'viaPoint' || currentRoute?.type === 'inProgress'
    );
  }

  /**
   * 経由地の数
   */
  public get viaPointLength(): number {
    return this.shipment?.route?.viaLocationList?.length ?? 0;
  }

  /**
   * 到着済経由地の数
   */
  public get passedViaPointLength(): number {
    const routePointList: RoutePoint[] = this.dataSource.data;
    if (!routePointList) {
      return 0;
    }

    const passedRouteViaPointList = routePointList.filter(
      (routePoint) => routePoint.type === 'viaPoint' && routePoint.completed,
    );
    return passedRouteViaPointList.length;
  }

  /**
   * 未到着経由地の数
   */
  public get notPassedViaPointLength(): number {
    const viaLocationList = this.shipment?.route.viaLocationList;
    if (!viaLocationList) {
      return 0;
    }
    return viaLocationList.length - this.passedViaPointLength;
  }

  private initRoute(): void {
    const shipmentRoute = this.shipment?.route;

    if (!shipmentRoute?.startLocation || !shipmentRoute.arrivalLocation) {
      return;
    }

    // 開始地点, 経由地, 到着地点の順で変換する
    let route: RoutePoint[] = [];
    const startPoint = this.createRoutePoint(
      shipmentRoute.startLocation,
      'startPoint',
      this.shipment?.startDatetime,
    );
    route.push(startPoint);

    for (const location of shipmentRoute?.viaLocationList || []) {
      const viaPoint = this.createRoutePoint(location, 'viaPoint');
      route.push(viaPoint);
    }

    const arrivalPoint = this.createRoutePoint(
      shipmentRoute.arrivalLocation,
      'arrivalPoint',
      this.shipment?.arrivalDatetime,
    );
    route.push(arrivalPoint);

    // ステータス履歴を古い順にソートする
    const shipmentStatusHistory = this.shipment?.statusHistory?.sort((a, b) =>
      isBefore(a.eventAt, b.eventAt) ? -1 : 1,
    );

    // ステータス履歴から状態を設定する
    route = this.setRoutePointStatus(route, shipmentStatusHistory);

    // ステータス履歴から現在位置を設定する
    route = this.setCurrentLocation(route, shipmentStatusHistory);

    // 経由地があれば、拡張ボタンを表示する
    // ボタンは、二つ目のポイントのタイプが経路上ならそこにボタンを表示する
    // それ以外ならボタンを追加する
    if (this.viaPointLength > 0) {
      if (route.length > 1 && route[1].type === 'inProgress') {
        route[1].displayButton = true;
      } else if (route.length > 0) {
        const buttonPoint: RoutePoint = {
          type: 'inProgress',
          displayButton: true,
          completed: route.length > 1 && route[1].completed,
        };
        route.splice(1, 0, buttonPoint);
      }
    }

    this.dataSource.data = route;
  }

  private createRoutePoint(
    location: ShipmentLocationViewModel,
    type: RoutePointType,
    timestamp?: string,
  ): RoutePoint {
    const routePoint: RoutePoint = {
      pointId: location.locationId,
      pointName: location.locationName,
      type: type,
      timestamp: timestamp,
    };
    return routePoint;
  }

  private setRoutePointStatus(
    route: RoutePoint[] = [],
    shipmentStatusHistory: ShipmentStatusViewModel[] = [],
  ): RoutePoint[] {
    if (route.length <= 0 || shipmentStatusHistory.length <= 0) {
      return route;
    }

    const startPoint = route.find((point) => point.type === 'startPoint');
    const arrivalPoint = route.find((point) => point.type === 'arrivalPoint');
    const viaPointMap: Record<string, RoutePoint[]> = {};
    for (const point of route) {
      if (point.type !== 'viaPoint' || !point.pointId) {
        continue;
      }

      if (!viaPointMap[point.pointId]) {
        viaPointMap[point.pointId] = [];
      }
      viaPointMap[point.pointId].push(point);
    }

    // 状態履歴を古い順に辿り、ルート位置の状態を更新していく
    for (const status of shipmentStatusHistory) {
      if (!status.locationId) continue;

      switch (status.status) {
        case ShipmentStatus.InProgress: {
          const viaPoint = viaPointMap[status.locationId]?.find(
            (point) =>
              point.arrivedAt !== undefined && point.departedAt === undefined,
          );

          if (viaPoint) {
            viaPoint.departedAt = status.eventAt;
            const arrivedAt = viaPoint.arrivedAt;
            const departedAt = viaPoint.departedAt;
            if (arrivedAt && departedAt) {
              viaPoint.stayDurationText = formatShipmentStayDuration(
                arrivedAt,
                departedAt,
              );
            }
          } else if (
            startPoint?.pointId === status.locationId &&
            startPoint.timestamp === this.shipment?.startDatetime
          ) {
            startPoint.completed = true;
            startPoint.timestamp = status.eventAt;
          }
          break;
        }
        case ShipmentStatus.AtStop: {
          const viaPoint = viaPointMap[status.locationId]?.find(
            (point) => point.arrivedAt === undefined,
          );

          if (viaPoint) {
            viaPoint.completed = true;
            viaPoint.timestamp = status.eventAt;
            viaPoint.arrivedAt = status.eventAt;
            if (viaPoint.departedAt === undefined) {
              viaPoint.stayDurationText = undefined;
            }
          }
          break;
        }
        case ShipmentStatus.AtDestination:
          if (arrivalPoint?.pointId === status.locationId) {
            arrivalPoint.completed = true;
            arrivalPoint.timestamp = status.eventAt;
            arrivalPoint.arrivedAt = status.eventAt;
          }
          break;
        case ShipmentStatus.Ready:
          if (startPoint?.pointId === status.locationId) {
            startPoint.completed = true;
          }
          break;
      }
    }

    return route;
  }

  private setCurrentLocation(
    route: RoutePoint[],
    shipmentStatusHistory: ShipmentStatusViewModel[] = [],
  ) {
    const currentLocationIndex = route
      .map((point) => point.completed)
      .lastIndexOf(true);
    if (currentLocationIndex !== -1) {
      // 終端ステータス以外で最後の状態の一時現在地を表示させる
      const lastStatus = shipmentStatusHistory
        .filter(
          (status) =>
            ![
              ShipmentStatus.Completed,
              ShipmentStatus.Deleted,
              ShipmentStatus.Interrupted,
            ].includes(status.status as ShipmentStatus),
        )
        .at(-1);
      if (lastStatus?.status === ShipmentStatus.InProgress) {
        const currentPoint: RoutePoint = {
          type: 'inProgress',
          currentLocation: true,
        };
        route.splice(currentLocationIndex + 1, 0, currentPoint);
      } else {
        const currentPoint = route[currentLocationIndex];
        currentPoint.currentLocation = true;
      }
    }

    return route;
  }
}
